import BattleActions from '../battle-data/battle-actions.js'
import {getGrowthPatterns} from './get-Combatant-growth-pattern'
import {randomFromArray} from '../../helpers/random-from-array.js'
import store from '../../init/store.js'


/* Return a Combatant with all of our battle properties */

const combatantModelDefaults = {
  name: "Combatant Name",
  loadoutTitle: "Default", //b5 specific. 1.0 will not be so boxed
  level: 1,
  xp: 0,
  class: "dps", //dps | disrupt | support
  skinId: "skin_m1", //Lookup in Skin assets

  characterUpgrades: [
    // FOR EXAMPLE:
    // {
    //   libraryId: "character-upgrade-001-i",
    //   instanceId: "upgrade_2347283746",
    //   isEquipped: true,
    //   acquiredTimestamp: 1495729113173 //timestamp when this item was acquired by Player
    // }
  ],

  animationCssRule: "none",

  attacks: [],
  items: [
    //These items are "brought to the table" when assembling a team
    //{actionId: "item_fake", instanceId: "itemInstance1", isStolen: true},
  ],

  ///////////////////////////////////

  hp: 50,
  maxHp: 50,
  pp: 50,
  maxPp: 50,

  //maxDangerMeter: 100,
  dangerMeter: 0, // 0 to 100

  committedTurnData: false, //object or false

  status: "normal", /* normal, memory-leak, lag, fire */

  healthStatPoints: 0, //Static Stat value of character (in battle)
  attackStatPoints: 0, //(attack + defense)
  // specialStatPoints: 0,
  speedStatPoints: 0,

  attackModifier: 0, //Adjustable modifiers
  defenseModifier: 0,
  //specialModifier: 0,
  speedModifier: 0,

  accuracyModifier: 0, //PERCENT. 1 = 1%. (Not actual fraction percent)

  isBot: false, //same idea as "isComputerControlled". new name.
  isChallenger: false,

  //Lagging
  laggingOutProbability: 60, //When status is "lag", % that the action will lag out. (Higher number = more likely to lag)

  //AI:
  isSelfServing: true, //false = teamServing
  personalitySmartAttempts: 2, //higher = more likely to make a personality based decision rather than falling back to regular
  personalityObviousOpportunityRating: 75, //0 to 100. higher = more likely to take advantage of obvious opportunities
                                          // (like weakness multiplayers EX: Scope Bomb -> on a Deadline enemy)
  computerAiPersonality: {
    default: {
      "regular": 1, //Plain Attack
      "vicious": 1, //Most damaging
      "recovering": 1, //Recover numbers
      "cursing": 1, //Offensive apply negative status
      "cleaning": 1, //Defense heal negative statuses
      "boosting": 1, //Defensive boost with positive statuses
      "slowing": 1, //Offensively decreasing enemy stats
      "thief": 1, //Anything dealing with affecting (or taking) enemy items
    },
    goodTimes: {
      "regular": 1,
      "vicious": 1,
      "recovering": 1,
      "cursing": 1,
      "cleaning": 1,
      "boosting": 1,
      "thief": 1,
      "slowing": 1,
    },
    badTimes: {
      "regular": 1,
      "vicious": 1,
      "recovering": 1,
      "cursing": 1,
      "cleaning": 1,
      "boosting": 1,
      "thief": 1,
      "slowing": 1,
    }
  },
  goodTimes: [75, 100], //min and max % of team HP
  badTimes: [0, 25], //min and max % of team HP

};


export default function (data = {}) {

  //Combine overrides with defaults
  let properties = {
    ...combatantModelDefaults,
    ...data,
  };

  //Check that all items have an instanceId.
  //(We could really just place the instanceIds here? Isn't that cleaner?)
  if (data.items) {
    data.items.forEach(item => {
      if (!item.instanceId) {
        console.warn('NO INSTANCE ID FOUND', item, data.name || "Unkown")
      }
    })
  }

  //Add stats
  const growthPatterns = getGrowthPatterns(properties.class);
  const maxHp = growthPatterns.hpGrowthPattern[properties.level];
  const maxPp = growthPatterns.ppGrowthPattern[properties.level];

  //Get all of my attacks that are not generic (in case that was passed in here... like Rejoiners)
  //or default to an empty array.
  const attacks = (data.attacks || []).filter(a => a.actionId !== "attack_generic-attack");
  //NOTE: we will re-add "attack_generic-attack" when returning the payload

  return {
    ...properties,

    //Stats based on level
    maxHp: maxHp,
    hp: (data.hp === 0) ? 0 : data.hp || maxHp, //support actual 0 value (watch out, it's falsy!)

    maxPp: maxPp,
    pp: (data.pp === 0) ? 0 : data.pp || maxPp,

    speedStatPoints: growthPatterns.spdGrowthPattern[properties.level],
    attackStatPoints: growthPatterns.atkGrowthPattern[properties.level],


    attacks: [
      {actionId: "attack_generic-attack"}, //Always have this added as first.
      ...attacks
    ],

    //Intro text will randomly choose from this array of strings:
    introMessages: properties.introMessages || [
      `${properties.name} is picking a fight!` //default
    ],


    //Methods....
    isAlive() {
      return this.hp > 0;
    },

    getTotalSpeed() {
      return this.speedStatPoints + this.speedModifier;
    },

    getTotalAttack() {

      let statusBonus = 0;

      if (this.status === "fury") {
        statusBonus = 5; //TODO: balance this! might need to be a percentage, or related to level. Just like "focus" status
      }

      return this.attackStatPoints + this.attackModifier + statusBonus;
    },

    getTotalDefense() {
      //Note: no "defense" stat in b5. Just per-battle bonuses
      const focusBonus = this.status === "focus" ? 3 : 0; //TODO: Balance this? 3 seems kinda random. Should it be a point per level or something?
      return this.defenseModifier + focusBonus;
    },

    /** Consider other factors when deciding how much damage to dish out */
    getAttackCalc(actionAffectTargetHpPoints = 0, targetModel, actionStatusMultiplier=null, hasTimedHitBonus=false, bypassCasterAttackStats=false) {

      if (bypassCasterAttackStats) {
        return actionAffectTargetHpPoints
      }

      let attackDamage = (this.getTotalAttack() * -1) + actionAffectTargetHpPoints; //negative number
      //Potentially apply a Timed Hit bonus
      if (hasTimedHitBonus) {
        attackDamage -= 1; //Add 1 extra attack stat point (this feels a little better)
          //TODO: well, idk. What about later in the game? This bonus means pretty close to nothing.
      }

      let defenseOffset = Math.round(targetModel.getTotalDefense() / 2);

      //status damage multiplayer (EX: Scope Bomb vs Deadline)
      if (actionStatusMultiplier && targetModel.status === actionStatusMultiplier[0]) { //format = [status, rate]
        attackDamage = Math.round( attackDamage * actionStatusMultiplier[1] ); //should be a rate, like 1.5
      }

      //Throttle defenseOffset - don't let it be higher than the attack damage.
      //(defenseOffset higher than attackDamage can cause moves to do 0 damage or REVERSE damage which HEALS the target!!!)
      if ( Math.abs(defenseOffset) >= Math.abs(attackDamage)) {
        // console.log('over', defenseOffset, attackDamage)
        //Just do 1 hp point of damage.
        return -1;
      }

      const total = attackDamage + defenseOffset; //bring attackDamage closer to 0 (because it's a negative number)
      // console.log('tot:', total, 'atk:', attackDamage, 'target def:', defenseOffset);
      return total;
    },

    /* This is referenced on every repetition to add a bit of Caster's attack rating */
    getRepetitionHitCalc(repDmg=0) {

      //Beef up each repetition with a fraction of attack stat
      const buffAmount = Math.round(this.getTotalAttack() / -4); //negative number, because hp damage numbers are negative

      //NOTE: target defense is NOT considered in this equation. TODO: [BALANCE] Consider if target's defense should be here.
      return repDmg + buffAmount;
    },

    /** Returns a Number that represents a percent */
    getTotalAccuracy(actionModel) {

      //If nailed the timed hit, return 100
      if (actionModel.actionMeta.hasTimedHitBonus) {
        return 100
      }

      //If "focus" status, return automatic 100
      if (this.status === "focus") {
        return 100
      }

      if (this.status === "fury") {
        return 86
      }

      //Floor accuracyModifier at -50. Don't let somebody get lower accuracy than that
      const useAccuracyModifier = (this.accuracyModifier < -50) ? -50 : this.accuracyModifier;

      //start X%, then modify from there.
      return 97 + useAccuracyModifier + actionModel.accuracyModifier;
    },

    getLaggingProbability(actionModel) {
      //NOTE: in the future, we can add character upgrades (or actionModel dependencies) that affect this number.
      //For now, just return it
      return this.laggingOutProbability; //high number = more likely to lag out.
    },

    /** Get list of team items by combining all combatants's items on my team */
    getTeamItems() {
      //In the future, if we want to go back to "individual items" just return my own items here.

      const combatants = store.getState().battle.combatants;
      const teams = store.getState().battle.teams;


      let teamItems = [];
      (teams[this.belongsToTeam].combatantIds || []).forEach(combId => {
        const model = combatants[combId];
        teamItems = [
          ...teamItems,
          ...model.items || []
        ]
      });

      return [...teamItems]
    },

    getAvailableActions(userOptions={}) {

      const options = {
        //defaults
        filterForPp: true,

        //user settings
        ...userOptions
      };

      const attacks = [...this.attacks].filter(atk => {
        const actionModel = BattleActions[atk.actionId];

        //Check PP (unless we've opted out of PP check via userOptions object)
        if (options.filterForPp && actionModel.ppCost > this.pp) {
          //return false or whatever. Haven't balanced PP yet
          return false
        }

        //If I have commited data, let the Revert move be available (it's a super)
        if (actionModel.useCasterCommittedData) {
          return this.committedTurnData; //return true if we have committed data, or false if there is no commit to use
        }

        //Next, check supers against danger meter
        if (actionModel.isSuper && !this.isDangerMeterReady()) {
          return false; //This action is a super but our danger meter is not ready
        }

        return true
      });
                                        //TODO: also filter out Supers when Danger Meter is not ready
      const items = this.getTeamItems(); //In the future, maybe change this to only "my" items (use the feature flag?)



      return [
        ...attacks,
        ...items,
      ]
    },

    /* Filter Available Actions for only the supers */
    getAvailableSupers() {
      return this.getAvailableActions().filter(atk => {
        const actionModel = BattleActions[atk.actionId];
        return actionModel.isSuper
      })
    },

    //Return all supers whether they are available or not.
    getAllSupers() {
      return this.attacks.filter(atk => {
        const actionModel = BattleActions[atk.actionId];
        return actionModel.isSuper
      });
    },

    //Used for the Submission menu. Return the name of my Super (first one if I have a lot)
    getSuperAction() {
      // const firstSuper = [...this.attacks].find(atk => {
      //   const actionModel = BattleActions[atk.actionId];
      //   return actionModel.isSuper
      // });

      const firstSuper = this.getAllSupers()[0];

      if (firstSuper) {
        return BattleActions[firstSuper.actionId]
      }
      return null //I'd hope you never see this
    },

    isDangerMeterReady() {
      return this.dangerMeter === 100
    },

    // getDangerMeterIncreaseBonus() { //TODO ? Maybe Combatants get a little boost for each DM increase?
    //   //return this.
    // },

    getIntroMessage() {
      return randomFromArray(this.introMessages)
    }

  }
}


// WEBPACK FOOTER //
// ./src/battle/models/CombatantModel.js